import isEqual from 'lodash/isEqual'
import {
    ChangeEvent, KeyboardEventHandler, useRef, useEffect, useCallback,
} from 'react'
import {
    NumberCell, Compatible,
} from '@silevis/reactgrid'
import {
    parseLocaleNumber,
} from 'app/shared-components/SkyNetSpreadSheet/customTemplates/NumberCellTemplate/parseLocalNumber'
import useSkyNetSpreadSheetContext from 'app/shared-components/SkyNetSpreadSheet/SkyNetSpreadSheetContext'
import useOutsideScroll from 'app/hooks/useOutsideScroll'
import useOutsideClick from 'app/hooks/useOutsideClick'

const stopPropagation = (e) => {
    return e.stopPropagation()
}

export default function SkyNetNumberCell({
    onChange, defaultValue, onBlur, onKeyDown,
}: Readonly<{
    onChange: (e: ChangeEvent<HTMLInputElement>, commit?: boolean) => void,
    onBlur: (e: ChangeEvent<HTMLInputElement>) => void,
    onKeyDown: (e: KeyboardEventHandler<HTMLInputElement>) => void,
    cell: Compatible<NumberCell>,
    value: number | string,
    defaultValue?: string,
}>) {
    const inputRef = useRef(null)
    const valueRef = useRef<number | string>(inputRef?.current?.value || defaultValue || '').current
    const {
        forceEditQuit,
    } = useSkyNetSpreadSheetContext()

    const onBlurSave = useCallback(() => {
        if (!isEqual(
            parseLocaleNumber(inputRef.current.value),
            parseLocaleNumber(String(valueRef)),
        )) {
            onBlur(({
                currentTarget: {
                    value: String(inputRef.current.value),
                },
            }) as unknown as ChangeEvent<HTMLInputElement>)
        } else {
            forceEditQuit()
        }
    }, [
        onBlur,
        valueRef,
        forceEditQuit,
    ])

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus()
            inputRef.current.setSelectionRange(
                inputRef.current.value.length,
                inputRef.current.value.length,
            )
        }
    }, [])

    useOutsideScroll(inputRef, onBlurSave)
    useOutsideClick(inputRef, onBlurSave)

    return (
        <input
            className="rg-input"
            inputMode="decimal"
            ref={inputRef}
            defaultValue={defaultValue}
            onChange={onChange}
            onBlur={onBlur}
            onKeyDown={onKeyDown as unknown as KeyboardEventHandler<HTMLInputElement>}
            onCopy={stopPropagation}
            onCut={stopPropagation}
            onPaste={stopPropagation}
            onPointerDown={stopPropagation}
        />
    )
}
